import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/mainLayout";
import "./style.scss";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../components/Loader";
import { getNDAStatus, ndaAccept } from "../../api/apis/nda";
import { Spinner } from "react-bootstrap";
import InputWithLabel from "../../components/inputWithLabel";
import FormLayout from "../../layouts/formLayout";

const Disclaimer = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [alreadyAccepted, setAlreadyAccepted] = useState(false);
  const location = useLocation();
  const isInstructions = location?.pathname === "/instructions";


  const getQuestions = async () => {
    try {
      setLoading(true);
      const quesRes = await getNDAStatus();
      setLoading(false);
      if (quesRes?.data?.data?.nda_accepted) {
        setAlreadyAccepted(true);
       !isInstructions && navigate("/location");
      }
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    getQuestions();
  }, []);

  const onNext = async () => {
    try {
      setBtnLoading(true);
      if (alreadyAccepted) {
        navigate("/location");
      } else {
        const acceptNdaResponse = await ndaAccept({ name: location.state?.name ?? name });
        setBtnLoading(false);
        if (acceptNdaResponse?.data?.status === "success") {
          navigate("/location");
        }
      }
    } catch (error) {
      setBtnLoading(false);
    }
  };

  const onNextToInstructions = () => {
    navigate("/instructions", { state: { name } });
  }

  const handleChangeName = (e) => {
    setName(e?.target?.value ?? {});
  }

  useLocation(() => {
    setName(location.state?.name ?? '')
  }, [location])

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <MainLayout progress={0} hrLine formTransparent>
          {isInstructions ? (
            <div className="instructions__section__main">
              <h3>Instructions for Completing the Assessment</h3>
              <div className="instructions__section">
                <p>
                  Please review the following key terminology and guidelines
                  before you begin to ensure a smooth and accurate assessment
                  process.
                </p>
                <h4>Terminology Clarification:</h4>
                <ul>
                  <li>
                    <span>"Building"</span> – Refers to the physical structure
                    housing the coworking space.
                  </li>
                  <li>
                    <span>"Workspace Location/Location/Coworking"</span> –
                    Refers specifically to the coworking facility or office
                    within the building.
                  </li>
                </ul>
                <h4>Assessment Structure</h4>
                <ul>
                  <li>
                    The assessment is divided into <span>eight categories</span>
                    , each with a brief description to provide context on what
                    is being assessed.
                  </li>
                  <li>
                    Within each category, you will find multiple assessment
                    questions.
                  </li>
                </ul>
                <p>
                  Some questions do not contribute to the final assessment score
                  or grade. These are marked as <span>“optional”</span> and are
                  intended for data collection purposes. While you may skip
                  these, they represent valuable insights sought by clients and
                  brokers, so we encourage you to complete them if possible.
                </p>
                <div className="instruction__white__box">
                  <div className="white__box__header">
                    <h2>General Guidelines</h2>
                  </div>
                  <div className="white__box__body">
                    <h4>Complete All Required Queries</h4>
                    <ul>
                      <li>
                        All required questions must be answered. Required
                        questions are marked with * on the left navigation menu
                        bar.
                      </li>
                      <li>
                        If any required fields are left unanswered, the
                        assessment will be considered incomplete and you will
                        not be able to submit the assessment to be scored or
                        graded.
                      </li>
                      <li>
                        Provide accurate and detailed responses to all inquiries
                      </li>
                    </ul>
                    <h4>Additional Fields & Attachments</h4>
                    <ul>
                      <li>
                        Try to provide complete and detailed responses in all{" "}
                        <span>open-text fields.</span>
                      </li>
                      <li>
                        Include any <span>supporting documents</span> and
                        information where possible.
                      </li>
                    </ul>
                    <h4>Navigation</h4>
                    <ul>
                      <li>
                        Use the <span>navigation bar</span> on the left to move
                        between categories and questions.
                      </li>
                      <li>
                        Click the <span>"Main Page"</span> button to return to
                        your location list, check progress, or switch to a
                        different location.
                      </li>
                      <li>
                        After answering each question, click <span>"Next"</span>{" "}
                        to save and proceed.
                      </li>
                      <li>
                        If unsure about an answer, you may skip it and revisit
                        skipped questions later using the{" "}
                        <span>"Go to Next Skipped Question"</span> button.
                      </li>
                    </ul>
                    <p>
                      Once you’ve reviewed the instructions, you’re ready to
                      begin the assessment. Click "Next" to proceed.
                    </p>
                    <h4 className="mt-3">Contact Us</h4>
                    <p>
                      If you need any assistance, feel free to reach out to us
                      at{" "}
                      <a href="mailto:info@flexgrade.io">info@flexgrade.io</a>.
                      We’ll get back to you within one business day.
                    </p>
                    <div className="next__and__skip">
                      <button disabled={btnLoading} onClick={onNext}>
                        {btnLoading ? (
                          <Spinner animation="border" role="status" />
                        ) : (
                          "NEXT"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <FormLayout
              heading={
                "Non-Disclosure Acknowledgment"
              }
              whiteBg
              onClick={onNextToInstructions}
              disabled={(!name || name === "") && !isInstructions}
            >
              <div className="disclaimer__section">
                <p>
                  By entering your name below, you acknowledge and agree to:
                </p>
                <br />
                <ul>
                  <li>
                    <span>Maintain strict confidentiality</span> regarding all non-public information about FlexGrade, including its platform, products, services, assessments, business operations, and proprietary information.
                  </li>
                  <li>
                    <span>Use all disclosed information solely for the agreed-upon business purposes</span> and never for personal or commercial gain.
                  </li>
                  <li>
                    <span>Refrain from sharing confidential information</span> with anyone except authorized team members whose names have been communicated to FlexGrade in advance.
                  </li>
                  <li>
                    <span>Take reasonable precautions to protect confidential information</span>, using the same level of care as you would for your own confidential materials.
                  </li>
                  <li>
                    <span>Never copy, reverse-engineer, modify, decompile, or distribute proprietary materials</span>, including software, processes, or technical information.
                  </li>
                  <li>
                    <span>Comply with all applicable laws and regulations</span>, including U.S. export control laws, when handling FlexGrade’s confidential information.
                  </li>
                  <li>
                    <span>Immediately return or destroy all confidential materials</span> upon request from FlexGrade.
                  </li>
                  <li>
                    <span>Notify FlexGrade immediately</span> in the event of any unauthorized disclosure or security breach.
                  </li>
                  <li>
                    <span>Acknowledge that confidentiality obligations remain in effect indefinitely</span>, even after discussions or business engagements with FlexGrade have ended.
                  </li>
                </ul>
                <div className="mt-4">
                  <InputWithLabel
                    labelFontWeight="600"
                    onChange={handleChangeName}
                    labelFont={11}
                    inputFont={18}
                    placeholder={"Your full name"}
                    label={"ENTER FULL NAME (FIRST NAME AND LAST NAME)"}
                  />
                </div>
              </div>
            </FormLayout>
          )}
        </MainLayout>
      )}
    </>
  );
};

export default Disclaimer;
